/**
 * Every address in the app, in one place, in one language (F-142).
 *
 * **It lives in `@nabu/shared` since F-157, and it used to live in `packages/web/src`.** The
 * browser is no longer the only thing that has to know where a screen is: the cross-device
 * hand-off mints a credential that carries a destination, and the API is what turns that
 * destination into a `Location` header. A redirect target is exactly the wrong thing to hold
 * two copies of — the copy that drifts is the one that sends a learner to a 404 with a fresh
 * session in their cookie jar, or, if the drift were ever in the other direction, the one that
 * redirects somewhere nobody vetted. `install.ts` makes the same argument about the funnel's
 * vocabulary and for the same reason: the words travel over HTTP between a page and a Lambda.
 *
 * `packages/web/src/routes.ts` is a re-export, so every screen still imports `./routes.js` and
 * nothing in the browser package changed shape.
 *
 * Leo, 2026-09-17: *"Im logged in and the ui is in English but the slug says investigacion."*
 *
 * **The locale belongs in the content, not in the address.** The sections were named in
 * Spanish because the product's first language is Spanish, which was true and is still the
 * wrong place to say it: the interface is translated at render time from `i18n.ts`, so an
 * English-speaking learner reads an English screen at a Spanish URL — and translating the
 * slugs per locale would be worse again, because the same screen would then have two
 * addresses and neither could be shared, bookmarked or written into a support reply without
 * asking first which language the reader uses. One stable path per screen, the same for
 * everybody, with the language living entirely in what the page says.
 *
 * English rather than Spanish for that stable form because the rest of the vocabulary a URL
 * already shares — `/admin`, `/chat`, `/plan`, every route the API answers — is English, and
 * a half-translated address bar is exactly what produced the complaint.
 *
 * **Every old path still works**, redirected in `App.tsx` with its query string and fragment
 * intact. They are in browser histories, in home screens saved before today and in anything
 * anybody bookmarked, and a 404 at a URL that worked yesterday is indistinguishable from the
 * app being broken.
 */
export const ROUTES = {
    home: '/',
    signIn: '/sign-in',
    chat: '/chat',
    progress: '/progress',
    /**
     * The objective: what is being researched, what was found, and what is being taught. F-141.
     *
     * **One address, because there is one objective.** `/research` used to sit beside this and
     * the two were two answers to the same question — a learner does not have a research task
     * and a plan task, they have one thing they are learning, which is read up on and then
     * taught. The split was the pipeline's stages leaking into the navigation, exactly as the
     * *start research* and *build the curriculum* buttons were the pipeline's stages leaking into
     * the controls.
     *
     * `/plan` is the survivor rather than `/research`, and rather than a third name for the
     * merged thing:
     *
     * - It is the word for the whole object and not for one phase of it. Research is something
     *   that happens *to* a plan; a plan that has not been researched yet is still the learner's
     *   plan, and a plan three weeks into being taught still has research behind it. Naming the
     *   merged screen after the phase would have been the same mistake in one address instead of
     *   two.
     * - It is already the target of `/objetivos`, the redirect this app has carried longest.
     *   Retiring it would have left that pointing at a dead path — a redirect to a redirect, or
     *   a rewrite of an address that was retired before F-142 existed.
     * - It is where `Landing` already sends a learner with no objective and one that is finished,
     *   so the address in the bar stops changing as an objective moves through its life.
     */
    plan: '/plan',
    /**
     * Stating a plan, which is **not** a state of any plan. 2026-09-20.
     *
     * Leo: *"Adding a new plan must take you to a fully isolated and clean slate ui… when I'm in a
     * plan everything is about that plan, when I add a new one it's a clean slate, no progress no
     * nothing, each plan is a fully isolated namespace."*
     *
     * **An address of its own rather than a sentinel in `?o=`, and the reason is what `?o=` means.**
     * That parameter says *which plan this screen is about*, and the whole of this repository's plan
     * isolation is built on reading it that way — `sectionHref` refuses it to screens that are not
     * about one plan, `planPath` writes it, `Chat`, `Progress` and `Objectives` each read it to
     * decide what to render, `PlansProvider.current` remembers it so the global screens can keep
     * naming a plan, `sw.js` puts it in a notification and `resolveDestination` validates it out of
     * a hand-off. A reserved value in there would be the address claiming the screen is about a plan
     * called `__new__`, and every one of those readers would need teaching that one string is not an
     * id. The state being addressed is precisely the one that is about *no* plan, so it is a path
     * and carries no `?o=` at all.
     *
     * **It therefore cannot collide with an objective id**, and not merely by being unlikely to: an
     * objective id has exactly one spelling in an address — the value of `?o=` — and this is not a
     * value of anything. Even if a path segment ever became one, `OBJECTIVE_ID` below refuses a `/`
     * and `id('obj')` mints `obj_`-prefixed ids, so neither half of `/new-plan` could be mistaken for
     * one.
     *
     * **One segment and hyphenated rather than `/plan/new`**, which is the other obvious spelling
     * and was rejected twice over. `routes.test.ts` asserts every address is a plain one-segment
     * path, and relaxing that to nest one route would have traded a guard for a hierarchy the router
     * does not otherwise have. And the nesting would have been a lie in the address bar: a path
     * under `/plan` reads as a screen belonging to the plan, which is the exact confusion the
     * owner is complaining about.
     *
     * **Deliberately not in `SECTIONS`** (`nav.ts`), like `/welcome`: it is reached from the plan
     * selector, which is where the plans are, and a hamburger entry for it would be a permanent
     * link that says *add* to a learner at their cap. It is in the plan chrome's list all the same,
     * because the selector is how a learner leaves it — see `showsPlanChrome` and `showsPlanNav`.
     */
    newPlan: '/new-plan',
    /**
     * The first run: configuration inside the wait, then the worked examples. F-141.
     *
     * **An address of its own rather than a mode of `/plan`**, and the reason is the gate. A
     * learner with an objective and no reach window cannot reach `/plan` — that is the owner's
     * decision, taken as a gate rather than a nag — so the screen that fixes it has to be
     * somewhere `/plan` is not. Written as one route so the redirect has a target, the back
     * gesture has somewhere to land, and a learner who closes the tab can be sent straight back
     * to where they were.
     *
     * **Deliberately not in `SECTIONS`.** It is not a destination anybody navigates to on purpose:
     * it exists for as long as one setting is missing and then answers with a redirect for ever.
     * A menu entry for it would be a permanent link to a screen that bounces.
     *
     * **And deliberately not in `PLAN_SCREENS`** (F-151 step 8), so `sectionHref` never gives it a
     * `?o=`. Two reasons, and the first is the one that also keeps `/settings` out: what this screen
     * acquires is the **reach window**, which is global by Leo's decision — *"Messaging settings is
     * global, I define globally, NOT per objective"* — and an address naming a plan is a claim that
     * the screen acts on it. Second, it is the gate's redirect target, and a target that varied by
     * plan would be several addresses for one state.
     *
     * Nothing is lost by it either, though that is corroboration rather than a reason: a learner the
     * gate is closed on has one plan at every tier anybody is on. That last clause is load-bearing —
     * `ENTITLEMENTS` grants two at tier 2 and three at tier 3 — so it is a fact about who exists
     * today, not about the code, and the two reasons above are what this actually rests on.
     */
    welcome: '/welcome',
    settings: '/settings',
    profile: '/profile',
    /**
     * The one-click unsubscribe landing, and the one path deliberately left in Spanish.
     *
     * It is not a screen a learner navigates to: the API mints it into the footer of every
     * message it sends (`handler.ts`, the `List-Unsubscribe` header), so the addresses that
     * matter are the ones already sitting in inboxes. Renaming it would either break those or
     * leave two spellings of it forever, and neither buys a learner anything — nobody reads
     * this one out of an address bar, they arrive by tapping a link in mail they want stopped.
     */
    unsubscribe: '/baja',
};
/**
 * Where the addresses that used to exist now go.
 *
 * Keys are dead paths and values are live ones, and `App.tsx` mounts one redirect per entry
 * by mapping over this object rather than by writing them out — which is what actually stops
 * a rename landing without its redirect, and a 404 for everybody whose home screen still
 * holds the old icon.
 *
 * `routes.test.ts` holds the table and not the mounting: that every target is a real route,
 * that no path is both dead and live, that the renamed five are all here. **Whether this file
 * is wired into the router at all is unasserted** — `App.tsx` is not in the test-compiled set
 * (see `tsconfig.test.json`) and there is no DOM harness here, so the mapping above is a
 * convention held by reading rather than a guard held by a test.
 */
export const MOVED = {
    '/entrar': ROUTES.signIn,
    '/progreso': ROUTES.progress,
    '/ajustes': ROUTES.settings,
    '/perfil': ROUTES.profile,
    /*
     * The one screen the five sections were cut out of. It predates this rename and is kept
     * for the same reason as the rest of them.
     */
    '/objetivos': ROUTES.plan,
    /**
     * Research and data, merged into the plan it was always about (F-141).
     *
     * **Two entries for one retirement, and both are load-bearing.** `/research` is young — F-142
     * minted it on 2026-09-17 — and is in nobody's bookmarks, but it is in the browser history of
     * everybody who has used the app since, and it is what `Landing` sent a learner to for the
     * whole of a sixteen-minute wait: the address most likely to be sitting in a background tab.
     * `/investigacion` is older still and used to point at `/research`; re-pointed here rather
     * than left to chain through a path that no longer exists, because `MOVED` mounts one
     * redirect per entry and a value that is not a live route is a 404 with extra steps.
     */
    '/research': ROUTES.plan,
    '/investigacion': ROUTES.plan,
};
/*
 * **`SETTINGS_SCHEDULE` was here and is gone with the notice that was its only caller** (F-141).
 *
 * It named `/settings#schedule` for `SetupNotice`'s *"Set it up"* link, which is deleted: the
 * state that notice announced is a gate now, and a learner in it is on `/welcome` with the reach
 * window in front of them rather than following a link to it. Removing the constant is this
 * file's own rule applied to itself — its docstring said, of the notification switch, that *"an
 * exported name with no caller is surface nobody is maintaining"*, and after the deletion the
 * only thing referencing this one was a test asserting its value.
 *
 * **F-143's mechanism is untouched and still promised.** Every panel on the configuration screen
 * is addressable by fragment — `Disclosure` takes an `anchor`, opens itself when the fragment
 * names it (including a section the learner folded away weeks ago) and moves focus to it — and
 * `App.tsx` still passes `anchor="schedule"`. What went is one link and the constant that spelled
 * its target; `/settings#schedule` still works, exactly as `/settings#notifications` always has
 * without a constant of its own. The next thing that wants to link there adds the name back in
 * the same change as the link.
 */
// ---- Where a hand-off lands (F-157) ----
/**
 * The shape of an objective id, for the one place an id arrives as an address fragment.
 *
 * Deliberately narrower than `safeId` in `packages/api/src/deletion.ts`, which is guarding an
 * S3 prefix: this one only has to survive being put after `?o=` and read back by the router.
 * Same instinct though, and the same reason to write the check rather than to trust the
 * caller — the value reaches here from a browser, and a destination is something the API puts
 * in a `Location` header.
 */
const OBJECTIVE_ID = /^[A-Za-z0-9_-]{1,64}$/;
/**
 * The one way an untrusted "where was I" becomes a destination — or nothing at all.
 *
 * **`MOVED` is honoured rather than refused, and that is the half worth explaining.** A
 * destination can be a path this app no longer publishes: it comes from a browser, and a
 * browser can be running a bundle from before a rename, or be on a home-screen icon saved a
 * month ago. Refusing those would turn F-142's promise — every old address still works —
 * into "every old address still works, unless you arrived by hand-off". Resolving through
 * `MOVED` here means the credential is minted carrying the *live* path, so the redirect is
 * correct even if the app that minted it was not.
 *
 * Anything else is `undefined`, which is the whole point: the caller redirects to a value
 * from `ROUTES` or it does not redirect. A path is never taken on the strength of looking
 * like one.
 */
export function resolveDestination(path, objectiveId) {
    if (typeof path !== 'string' || !path)
        return undefined;
    /*
     * **`Object.hasOwn` before the lookup, and the type check after it.** A plain object literal
     * inherits from `Object.prototype`, so `MOVED['toString']` is a *function* and therefore
     * truthy: without this, five strings a caller can send — `toString`, `constructor`,
     * `valueOf`, `hasOwnProperty`, `__proto__` — walk past an allowlist whose whole job is to
     * refuse everything it does not recognise, and end up in the `Location` header as
     * `[object Object]`. The declared type says `Record<string, string>` and says it honestly
     * about the entries; it says nothing about the prototype chain, which is exactly the class
     * of hole a type system cannot see and a redirect allowlist cannot afford.
     *
     * The `typeof` check afterwards is the same guard stated a second way, so a future entry of
     * the wrong shape cannot reach a caller that is about to build a URL out of it.
     *
     * `hasOwnProperty.call` rather than `Object.hasOwn`, which reads better and is ES2022: this
     * module is bundled for the browser as well as for the Lambda, and the older form needs no
     * assumption about which Safari a learner is on.
     */
    const own = Object.prototype.hasOwnProperty.call(MOVED, path);
    const moved = own ? MOVED[path] : undefined;
    const live = Object.values(ROUTES).includes(path) ? path : moved;
    if (typeof live !== 'string' || !live.startsWith('/'))
        return undefined;
    if (objectiveId === undefined || objectiveId === null || objectiveId === '') {
        return { path: live };
    }
    if (typeof objectiveId !== 'string' || !OBJECTIVE_ID.test(objectiveId))
        return undefined;
    return { path: live, objectiveId };
}
/**
 * The address, as a relative path.
 *
 * `?o=` and not something new: it is the query `tabs.ts` already writes for a screen that is
 * about one plan, so a hand-off lands on the same address the learner would have got by
 * tapping the tab. Relative, so the caller decides the origin — an absolute URL assembled
 * here would be a second place `NABU_PUBLIC_ORIGIN` is spelled.
 */
export function destinationHref(destination) {
    return destination.objectiveId
        ? `${destination.path}?o=${encodeURIComponent(destination.objectiveId)}`
        : destination.path;
}
//# sourceMappingURL=routes.js.map